import React, { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Trash2, Copy } from "lucide-react";
import Button from "../../../../../common/Button/Button";
import { Toaster } from "../../../../../common/Toaster/Toaster";
import FoodItemCard from "../../../ui/meal-slot/FoodItemCard/FoodItemCard";
import StreamlinedFoodAddModal from "../../../ui/meal-slot/StreamlinedFoodAddModal";
import MealSlotEditorRow from "../../../ui/meal-slot/shell/MealSlotEditorRow";
import MealSlotSummaryCard from "../../../ui/meal-slot/shell/MealSlotSummaryCard";
import MealSlotIntakeTimeModal from "../../../ui/meal-slot/shell/MealSlotIntakeTimeModal";
import CopyMealOptionToClientPlanModal from './CopyMealOptionToClientPlanModal';
import CopyFoodAcrossMealTypesModal from './CopyFoodAcrossMealTypesModal';
import { copyFoodToMealTargets } from "../../../core/diet-plan/copyFoodToMealTargets";
import { useUtils } from "../../../../../context/UtilsContext";
import useDietPlanStructureEditor from "../../../core/diet-plan/useDietPlanStructureEditor";
import { newFoodId } from "../../../core/diet-plan/newFoodId";
import { getMealFoodId, getRecipeCatalogId, getRecipeDetails } from "../../../core/diet-plan/recipeId";
import { resolveScaledNutrition, scaleFoodForQuantityEdit } from "../../../core/nutrients/calculateMealNutrients";
import { useRecipeCatalog } from "../../../core/recipes/useRecipeCatalog";
import { useBackgroundSync } from "../../../../../hooks/useBackgroundSync";
import {
  formatOptionTitle,
} from "../../../core/utils/formatOptionTitle";

function DietMealSlotEditor({
  periodName,
  mealOptionIds,
  foodData = [],
  targetPlanId = 0,
  context,
  setAlertMessage,
  setAlertType,
  onPeriodDataChange,
  clientId = null,
  eventTypeId: eventTypeIdProp,
  selectedOption,
  alwaysExpanded = false,
  syncScopeKey = "",
  dataByPeriod = null,
  // Orange summary-card data ({ title, itemCount, caloriesLabel, macros }).
  // When given, this editor renders its own row with the card, and intake
  // time moves to the meal level: the card shows and edits the meal's
  // intake window, and recipes no longer carry their own time.
  summary = null,
  // The meal's intake window in local "HH:MM" ({ start, end }, "" = unset).
  // Shared by every option of the meal type.
  intakeWindow = null,
  // ({ start, end }) => Promise — saves the window on the target plan meal.
  onSaveIntakeWindow = null,
}) {
  const slotTimeMode = Boolean(summary);
  const [selectedOptionTab, setSelectedOptionTab] = useState("Option 1");
  // `loading` no longer blocks the card list on add/edit/delete (those are
  // optimistic now); it still gates bulk delete, which is a deliberate batch op.
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(alwaysExpanded);
  const [isStreamlinedModalOpen, setIsStreamlinedModalOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const {
    recipes: searchResults,
    loading: searchLoading,
    totalCount: searchTotalCount,
  } = useRecipeCatalog(searchInputValue, {
    enabled: isStreamlinedModalOpen,
  });
  // null | "delete" | "copy" — the two bulk actions are mutually exclusive.
  const [selectMode, setSelectMode] = useState(null);
  const [selectedFoodIds, setSelectedFoodIds] = useState(new Set());
  const [copyToUserModalOpen, setCopyToUserModalOpen] = useState(false);
  const [copyAcrossMealTypesModal, setCopyAcrossMealTypesModal] = useState({
    open: false,
    foods: [],
  });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, message: "" });
  const [slotTimeModalOpen, setSlotTimeModalOpen] = useState(false);
  const checkedAutoOpenRef = useRef(new Set());

  const {
    dietCommonUtils,
    fetchDietCommonUtils,
    clearDietCommonUtilsCache,
  } = useUtils();

  const diet = useDietPlanStructureEditor({
    periodName,
    foodData,
    mealOptionIds,
    dietCommonUtils,
    fetchDietCommonUtils,
    clearDietCommonUtilsCache,
    setAlertMessage,
    setAlertType,
    context,
    targetPlanId,
    eventTypeId: eventTypeIdProp,
    clientId,
  });

  const { ensureOptionPlaceholder, loadOptionFromTemplateOnce } = diet;

  // ---------- background-sync engine ----------
  const scopeKey =
    syncScopeKey || (context === "plan" && targetPlanId ? `plan-${targetPlanId}` : "");
  const {
    enqueue,
    setOnIdle,
    withAutoRetry,
    willAutoRetry,
    resetRetries,
    pendingOps,
    reportCounts,
  } = useBackgroundSync(scopeKey, periodName);

  // localId -> the NEWEST pending edit payload for that food. Rapid saves
  // overwrite this instead of stacking, so the last edit always wins no
  // matter how the queued API calls interleave.
  const latestFoodUpdates = useRef(new Map());
  // Set by every op that changed server state; the post-drain finalize only
  // fetches when something actually ran.
  const dirty = useRef(false);

  // Publish this editor's pending/error counts (across all its option tabs) so
  // the Save-button pages can gate on the aggregate. useBackgroundSync clears
  // the entry on unmount.
  useEffect(() => {
    reportCounts(Object.values(diet.savedFoodItems).flat());
  }, [diet.savedFoodItems, reportCounts]);

  // Immutable patch/drop of one optimistic item by its localId.
  const patchItem = (tab, localId, patch) =>
    diet.setSavedFoodItems((prev) => ({
      ...prev,
      [tab]: (prev[tab] || []).map((it) =>
        it.localId === localId ? { ...it, ...patch } : it
      ),
    }));
  const dropItem = (tab, localId) =>
    diet.setSavedFoodItems((prev) => ({
      ...prev,
      [tab]: (prev[tab] || []).filter((it) => it.localId !== localId),
    }));

  // Read the latest tab contents without mutating (functional setState probe),
  // so reconcile logic sees post-reload state regardless of React batching.
  const readTab = (tab) =>
    new Promise((resolve) => {
      diet.setSavedFoodItems((prev) => {
        resolve(prev[tab] || []);
        return prev;
      });
    });

  // Mirror a card into the parent meal tree once it carries a real server id.
  // Send the WHOLE row, never an id-only patch: the parent subtracts the row's
  // previous scaled_nutrition and adds the incoming one, so a full row is
  // calorie-neutral while a partial one would subtract and never add back.
  const publishRowToParent = async (tab, localId) => {
    if (!onPeriodDataChange) return;
    const row = (await readTab(tab)).find((it) => it.localId === localId);
    if (!row || (row.deleteId ?? row.id) == null) return;
    await onPeriodDataChange({
      patchFood: { periodName, optionTab: tab, food: row },
    });
  };

  useEffect(() => {
    if (!selectedOption || selectedOption === "all") return;
    const title = formatOptionTitle(selectedOption);
    setSelectedOptionTab((current) => (current === title ? current : title));
  }, [selectedOption]);

  useEffect(() => {
    let mounted = true;
    const optionKey = `${periodName}-${selectedOptionTab}`;

    const loadAndCheckOpen = async () => {
      ensureOptionPlaceholder(selectedOptionTab);

      try {
        const loadedItems = await loadOptionFromTemplateOnce(selectedOptionTab);

        if (
          mounted &&
          !checkedAutoOpenRef.current.has(optionKey)
        ) {
          checkedAutoOpenRef.current.add(optionKey);
          if (Array.isArray(loadedItems) && loadedItems.length > 0) {
            setIsOpen(true);
          }
        }
      } catch (error) {
        console.error("Error loading recipe items:", error);
        if (mounted) {
          setAlertMessage?.("Error loading recipe items");
          setAlertType?.("error");
        }
      }
    };

    loadAndCheckOpen();

    return () => {
      mounted = false;
    };
  }, [
    selectedOptionTab,
    foodData,
    periodName,
    ensureOptionPlaceholder,
    loadOptionFromTemplateOnce,
    setAlertMessage,
    setAlertType,
  ]);

  // Bulk select is scoped to whatever's currently on screen. The option/meal-type
  // filter that drives periodName/selectedOptionTab lives in the parent and isn't
  // disabled during select mode, so a filter switch mid-selection would otherwise
  // leave the toolbar counting rows that aren't even in the visible list anymore
  // (and confirming against them would silently do nothing). Reset whenever the
  // visible slot changes, not just when the user explicitly cancels.
  useEffect(() => {
    setSelectMode(null);
    setSelectedFoodIds(new Set());
  }, [periodName, selectedOptionTab]);

  const openAddRecipeModal = () => {
    setSearchInputValue("");
    setIsStreamlinedModalOpen(true);
  };

  const closeStreamlinedModal = () => {
    setIsStreamlinedModalOpen(false);
    setSearchInputValue("");
    diet.setFoodState({
      activeFoodItem: {},
      gramsInputValue: "",
      timeValue: "",
      quantityType: "",
      availableQuantityTypes: [],
    });
  };

  const handleStreamlinedFoodSelect = async (recipe) => {
    const recipeId = getMealFoodId(recipe) || getRecipeCatalogId(recipe);
    if (!recipeId) {
      setAlertMessage?.("Invalid recipe selected.");
      setAlertType?.("error");
      return;
    }

    try {
      const detail = await diet.fetchDetailIfNeeded(recipe);
      if (!detail) {
        setAlertMessage?.("Could not load recipe details. Please try again.");
        setAlertType?.("error");
        return;
      }

      const normalized = {
        ...diet.normalizeDetailForModal(detail),
        food_id: recipeId,
        recipe_name: recipe.recipe_name,
      };
      const availableUnits =
        normalized.availableQuantityTypes ??
        Object.keys(normalized.quantity_conversions?.conversions || {});
      const defaultUnit = availableUnits[0] || "g";

      diet.setFoodState({
        activeFoodItem: normalized,
        gramsInputValue: "1",
        timeValue: "",
        quantityType: defaultUnit,
        availableQuantityTypes:
          availableUnits.length > 0 ? availableUnits : [defaultUnit],
      });
    } catch (error) {
      console.error("Error loading recipe details:", error);
      setAlertMessage?.("Error loading recipe details. Please try again.");
      setAlertType?.("error");
    }
  };

  // Returns true only when a FRESH server snapshot was actually merged. Callers
  // use that to decide whether it is safe to stop protecting optimistic rows —
  // a reload that quietly did nothing must not be read as "the server says this
  // row is gone", which is how added foods used to disappear from the list.
  const reloadOptionTabs = async (optionTabs = null) => {
    const tabs = (
      Array.isArray(optionTabs) && optionTabs.length
        ? optionTabs
        : [selectedOptionTab]
    ).filter(Boolean);

    if (context === "plan" && targetPlanId) {
      const planData = await diet.refreshPlanFoods(
        targetPlanId,
        tabs,
        clientId
      );
      const adopted = await onPeriodDataChange?.({ planData });
      // Either the per-tab merge landed, or the parent adopted a fresh payload.
      return Boolean(planData) || adopted === true;
    }
    if (context === "template") {
      // Parent refetch returns fresh food rows — pass them in so we don't merge
      // against the stale foodData closure (that race used to wipe new cards).
      const refreshed = await onPeriodDataChange?.();
      const freshFoods = refreshed?.foodData;
      for (const tab of tabs) {
        await diet.loadOptionFromTemplateOnce(tab, freshFoods);
      }
      return Boolean(freshFoods);
    }
    return (await onPeriodDataChange?.()) === true;
  };

  const afterRecipeMutation = async (optionTabs = null) => {
    await reloadOptionTabs(optionTabs);
    setIsOpen(true);
  };

  // Reconcile reload for a specific tab (tab-parameterized afterRecipeMutation,
  // safe to call from background runs where selectedOptionTab may have changed).
  const reloadTab = async (tab) => {
    await reloadOptionTabs([tab]);
  };

  // A row is unresolved when the client could not learn the truth from the
  // mutation itself: the op failed, or it succeeded without the server echoing
  // an id back. Those are the only cases a reconciling fetch actually answers.
  const isUnresolved = (it) => {
    const status = it._sync?.status;
    if (!status) return false;
    if (status === "error") return true;
    return (it.deleteId ?? it.id) == null;
  };

  // The single reconciliation point: never runs mid-stack — only once ALL
  // per-item queues have drained. Each mutation already echoes its row id back
  // and publishes it to the parent tree, so the common case needs no fetch at
  // all — we just release the 'synced' markers. We only go to the server when
  // something is genuinely unresolved, which also keeps a fetch from racing a
  // delete the user just made.
  const finalizeFoodSync = async () => {
    if (!dirty.current) return;
    dirty.current = false;
    // Probe current option keys — selectedOptionTab may have changed since the
    // edit that set dirty (e.g. user switched options mid-sync).
    const { tabs, items } = await new Promise((resolve) => {
      diet.setSavedFoodItems((prev) => {
        resolve({ tabs: Object.keys(prev), items: Object.values(prev).flat() });
        return prev;
      });
    });
    try {
      if (items.some(isUnresolved)) {
        const reloaded = await reloadOptionTabs(tabs.length ? tabs : [selectedOptionTab]);
        // Releasing a 'synced' marker hands the row's survival over to the
        // server snapshot. Only do that once we actually have one — otherwise
        // the row is left unprotected and the next merge deletes a good card.
        if (!reloaded) {
          dirty.current = true;
          return;
        }
      }
      diet.setSavedFoodItems((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((t) => {
          next[t] = (next[t] || []).map((it) =>
            it._sync?.status === "synced" ? { ...it, _sync: undefined } : it
          );
        });
        return next;
      });
    } catch (err) {
      console.error("Food sync reconcile failed:", err);
      dirty.current = true;
    } finally {
      // Edits that landed during reconcile need another idle pass.
      if (dirty.current) setOnIdle(() => finalizeFoodSync());
    }
  };
  useEffect(() => {
    setOnIdle(() => finalizeFoodSync());
  });

  // Build an optimistic card + a preconfigured-food snapshot from the current
  // modal state, so the item can render instantly and the ADD can fire later.
  const buildOptimisticFromFoodState = (localId) => {
    const { activeFoodItem, gramsInputValue, quantityType, timeValue: modalTime } =
      diet.foodState;
    // Meal-level window: recipes don't carry their own time.
    const timeValue = slotTimeMode ? null : modalTime;
    const recipeId = getMealFoodId(activeFoodItem);
    if (!activeFoodItem || !recipeId || !gramsInputValue) return null;
    const quantity = Number(gramsInputValue) || gramsInputValue;
    const cv = diet.calculatedValues || {};
    const fromModal = {
      proteins: Number(cv.protein) || 0,
      carbs: Number(cv.carbs) || 0,
      fats: Number(cv.fats) || 0,
      fiber: Number(cv.fiber) || 0,
      sugars: Number(cv.sugar ?? cv.sugars) || 0,
      calories: Number(cv.calories) || 0,
    };
    // Prefer detail scaling when modal calc is 0 (missing quantity_conversions).
    const scaled =
      resolveScaledNutrition(
        { quantity, quantity_type: quantityType, scaled_nutrition: fromModal },
        activeFoodItem,
        quantity,
        quantityType
      ) || fromModal;
    const item = {
      deleteId: null,
      id: null,
      localId,
      food_id: recipeId,
      food_name: activeFoodItem.food_name,
      common_name: activeFoodItem.common_name || "",
      recipe_details: activeFoodItem,
      food_details: {
        main_details: {
          food_name: activeFoodItem.food_name,
          common_names: activeFoodItem.common_name || "",
          calories_calculated_for: activeFoodItem.calories_calculated_for || 100,
        },
        measures: (activeFoodItem.measures || []).map((m) => ({
          unit_option_name: m.unit_option_name,
          basic_unit_measure: m.basic_unit_measure || 1,
          serving_size_unit: m.serving_size_unit || "g",
        })),
        nutrition: [],
      },
      scaled_nutrition: scaled,
      quantity,
      quantity_type: quantityType,
      quantity_type_id: activeFoodItem.unitToIdMap?.[quantityType] || null,
      unitToIdMap: activeFoodItem.unitToIdMap || {},
      intake_time: timeValue || null,
      _sync: { localId, status: "syncing", op: "create", error: null },
    };
    const snapshot = {
      ...activeFoodItem,
      food_id: recipeId,
      quantity: gramsInputValue,
      quantity_type: quantityType,
      intake_time: timeValue,
    };
    return { item, snapshot, recipeId };
  };

  const runCreate = async (localId, tab, recipeId, snapshot) => {
    const res = await diet.addFood({
      selectedOptionTab: tab,
      preconfiguredFood: snapshot,
      silent: true,
    });

    // The server already has this food in the option (raced add, or a row the
    // client hadn't loaded). An ADD can never succeed — pull the authoritative
    // row and re-apply this add as an UPDATE of it.
    if (res?.duplicate) {
      const queued = pendingOps.current.get(localId);
      pendingOps.current.delete(localId);
      latestFoodUpdates.current.delete(localId);
      await reloadTab(tab);
      const rows = await readTab(tab);
      dropItem(tab, localId); // remove the temp card; the real row is authoritative
      // added-then-deleted before the conflict surfaced: net intent is "no
      // change" — never delete the pre-existing server row.
      if (queued?.type === "delete") return;
      const existing = rows.find(
        (it) =>
          it.localId !== localId &&
          String(it.food_id) === String(recipeId) &&
          it._sync?.status !== "deleting"
      );
      if (!existing) return;
      const exId = existing.localId;
      const detail = getRecipeDetails(existing);
      const quantity = Number(snapshot.quantity) || existing.quantity;
      const unitName = snapshot.quantity_type || existing.quantity_type;
      const updatedFood = {
        ...existing,
        quantity,
        quantity_type: unitName,
        intake_time: snapshot.intake_time ?? existing.intake_time,
      };
      latestFoodUpdates.current.set(exId, updatedFood);
      patchItem(tab, exId, {
        quantity,
        quantity_type: unitName,
        intake_time: updatedFood.intake_time,
        scaled_nutrition: resolveScaledNutrition(
          { quantity, quantity_type: unitName },
          detail,
          quantity,
          unitName
        ),
        _sync: { localId: exId, status: "syncing", op: "update", error: null },
      });
      resetRetries(exId);
      enqueue(exId, withAutoRetry(exId, () => runUpdate(exId, tab)));
      return;
    }

    if (!res?.success) {
      const queued = pendingOps.current.get(localId);
      if (queued?.type === "delete") {
        // never persisted, and the user already asked to delete it — drop silently
        pendingOps.current.delete(localId);
        dropItem(tab, localId);
        return;
      }
      if (!willAutoRetry(localId)) {
        patchItem(tab, localId, {
          _sync: { localId, status: "error", op: "create", error: "Failed to add food item.", snapshot },
        });
      }
      throw new Error("add failed");
    }

    // The API already echoed the new row back — adopt its id straight onto the
    // optimistic card so it looks like a fetched row (id, deleteId and all).
    // The post-drain fetch still reconciles the authoritative row.
    dirty.current = true;
    const serverId = newFoodId(res);
    patchItem(tab, localId, {
      ...(serverId ? { id: serverId, deleteId: serverId } : {}),
      _sync: { localId, status: "synced", op: "create", error: null },
    });
    // The card's selection key is deleteId||id||localId — it just flipped from
    // localId to serverId above. Migrate any existing selection so a checkbox
    // ticked while this row was still saving doesn't silently drop out from
    // under the user (and leave a stale, uncounted entry behind).
    if (serverId) {
      setSelectedFoodIds((prev) => {
        if (!prev.has(localId)) return prev;
        const next = new Set(prev);
        next.delete(localId);
        next.add(serverId);
        return next;
      });
    }
    // The parent meal tree still holds this row with id: null, and
    // buildCardRowFromFood discards any row without one — so the card would be
    // dropped by the next merge. Publish the id-bearing row upward so both
    // copies agree without waiting on (or depending on) a refetch.
    await publishRowToParent(tab, localId);

    const queued = pendingOps.current.get(localId);
    if (!queued) return;

    // user deleted/edited before the create confirmed — replay it now that the id
    // is known, against the same localId the queued op was recorded under.
    pendingOps.current.delete(localId);
    if (!serverId) return;

    if (queued.type === "delete") {
      patchItem(tab, localId, {
        _sync: { localId, status: "deleting", op: "delete", error: null },
      });
      resetRetries(localId);
      enqueue(localId, withAutoRetry(localId, () => runDelete(localId, tab, serverId)));
    } else if (queued.type === "update") {
      // Send the freshest deferred edit, stamped with the new id so the
      // payload matches what a fetch would have produced.
      const current = (await readTab(tab)).find((it) => it.localId === localId);
      const latest = latestFoodUpdates.current.get(localId);
      const merged = {
        ...current,
        ...(latest || queued.payload),
        deleteId: serverId,
        id: serverId,
        localId,
      };
      latestFoodUpdates.current.set(localId, merged);
      patchItem(tab, localId, {
        ...queued.payload,
        _sync: { localId, status: "syncing", op: "update", error: null },
      });
      resetRetries(localId);
      enqueue(localId, withAutoRetry(localId, () => runUpdate(localId, tab)));
    }
  };

  // Coalescing runner: always sends the NEWEST edit for this food. Rapid saves
  // collapse — the first queued run takes the latest payload; later runs find
  // nothing left to send. The last edit always wins.
  const runUpdate = async (localId, tab) => {
    const job = latestFoodUpdates.current.get(localId);
    if (!job) return; // an earlier run already sent the newest values
    const res = await diet.updateFood({ selectedOptionTab: tab, updatedFood: job });
    if (!res || res.success === false) {
      if (!willAutoRetry(localId)) {
        patchItem(tab, localId, {
          _sync: { localId, status: "error", op: "update", error: "Failed to update food item.", payload: job },
        });
      }
      throw new Error("update failed");
    }
    dirty.current = true;

    // The server upserts on update: the row we just modified may now live
    // under a NEW id. Adopt it immediately so a chained edit targets the live
    // row instead of the retired id (which the server would just "skip").
    const churnedId = newFoodId(res);
    const idChanged = churnedId && String(churnedId) !== String(job.deleteId ?? job.id);
    const idPatch = idChanged ? { id: churnedId, deleteId: churnedId } : {};

    const pending = latestFoodUpdates.current.get(localId);
    if (pending === job) {
      latestFoodUpdates.current.delete(localId);
      // 'synced' keeps the row protected until the final fetch clears it.
      patchItem(tab, localId, {
        ...idPatch,
        _sync: { localId, status: "synced", op: "update", error: null },
      });
    } else {
      // A newer edit arrived mid-flight; keep the syncing marker — the queued
      // follow-up run will send it. Point it at the live row id.
      if (idChanged) {
        if (pending) latestFoodUpdates.current.set(localId, { ...pending, ...idPatch });
        patchItem(tab, localId, idPatch);
      }
    }
    // Keep the parent tree on the live id too, so a later delete targets the
    // current row rather than the one the upsert retired.
    await publishRowToParent(tab, localId);
  };

  const runDelete = async (localId, tab, serverId) => {
    const ok = await diet.deleteFood({ selectedOptionTab: tab, deleteId: serverId });
    if (!ok) {
      if (!willAutoRetry(localId)) {
        patchItem(tab, localId, {
          _sync: { localId, status: "error", op: "delete", error: "Failed to delete food item." },
        });
      }
      throw new Error("delete failed");
    }
    // Safe to drop immediately: no fetch runs until every queue drains, so
    // nothing can resurrect the row before the server state is final.
    dirty.current = true;
    latestFoodUpdates.current.delete(localId);
    dropItem(tab, localId);
  };

  const retryFoodSync = (item) => {
    const tab = selectedOptionTab;
    const localId = item.localId;
    const op = item._sync?.op;
    resetRetries(localId);
    if (op === "create") {
      patchItem(tab, localId, { _sync: { localId, status: "syncing", op: "create", error: null } });
      enqueue(localId, withAutoRetry(localId, () => runCreate(localId, tab, item.food_id, item._sync?.snapshot || item)));
    } else if (op === "update") {
      latestFoodUpdates.current.set(localId, item._sync?.payload || item);
      patchItem(tab, localId, { _sync: { localId, status: "syncing", op: "update", error: null } });
      enqueue(localId, withAutoRetry(localId, () => runUpdate(localId, tab)));
    } else if (op === "delete") {
      patchItem(tab, localId, { _sync: { localId, status: "deleting", op: "delete", error: null } });
      enqueue(localId, withAutoRetry(localId, () => runDelete(localId, tab, item.deleteId || item.id)));
    }
  };

  const handleStreamlinedAdd = () => {
    const localId =
      (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
      `tmp-${Date.now()}-${Math.round((typeof performance !== "undefined" ? performance.now() : 0))}`;
    const built = buildOptimisticFromFoodState(localId);
    if (!built) {
      setAlertMessage?.("Please enter a valid quantity.");
      setAlertType?.("warning");
      return;
    }
    const tab = selectedOptionTab;

    // The server keys foods per option — a second ADD of the same food is
    // rejected ("Food with key ... exists"). If the food is already in this
    // option, route the values through the update flow on the existing row.
    const existing = (diet.savedFoodItems[tab] || []).find(
      (it) =>
        String(it.food_id) === String(built.recipeId) &&
        it._sync?.status !== "deleting"
    );
    if (existing) {
      setIsOpen(true);
      closeStreamlinedModal();
      handleUpdateRecipeItem({
        ...existing,
        quantity: built.item.quantity,
        quantity_type: built.item.quantity_type,
        quantity_type_id: built.item.quantity_type_id || existing.quantity_type_id,
        intake_time: built.item.intake_time ?? existing.intake_time,
      });
      setAlertMessage?.("Food already in this option — its quantity was updated instead.");
      setAlertType?.("success");
      return;
    }

    // instant insert + close the modal — no network wait
    diet.setSavedFoodItems((prev) => ({
      ...prev,
      [tab]: [...(prev[tab] || []), built.item],
    }));
    // Push into parent meal tree so toolbar calories update immediately.
    onPeriodDataChange?.({
      patchFood: {
        periodName,
        optionTab: tab,
        food: built.item,
      },
    });
    setIsOpen(true);
    closeStreamlinedModal();
    dirty.current = true;
    enqueue(
      localId,
      withAutoRetry(localId, () => runCreate(localId, tab, built.recipeId, built.snapshot))
    );
  };

  const startSelectMode = (mode) => {
    setSelectMode(mode);
    setSelectedFoodIds(new Set());
  };

  const cancelSelectMode = () => {
    setSelectMode(null);
    setSelectedFoodIds(new Set());
  };

  const toggleFoodSelection = (foodId) => {
    setSelectedFoodIds((prev) => {
      const next = new Set(prev);
      if (next.has(foodId)) next.delete(foodId);
      else next.add(foodId);
      return next;
    });
  };

  const requestBulkDeleteConfirm = () => {
    if (selectedFoodIds.size === 0) return;
    const tab = selectedOptionTab;
    const rows = diet.savedFoodItems[tab] || [];
    const selected = Array.from(selectedFoodIds);
    let message;
    if (selected.length === 1) {
      const row = rows.find(
        (it) => String(it.deleteId || it.id || it.localId) === String(selected[0])
      );
      const name =
        row?.food_name || row?.food_details?.main_details?.food_name || "this recipe";
      message = `Are you sure you want to delete ${name}? This action cannot be undone.`;
    } else {
      message = `Are you sure you want to delete ${selected.length} recipes? This action cannot be undone.`;
    }
    setDeleteConfirm({ open: true, message });
  };

  const performBulkDelete = async () => {
    if (selectedFoodIds.size === 0) {
      setDeleteConfirm({ open: false, message: "" });
      return;
    }

    const tab = selectedOptionTab;
    const selected = Array.from(selectedFoodIds);
    const rows = diet.savedFoodItems[tab] || [];

    // Rows whose create is still in flight (or already failed) have no
    // server id yet — deleting them can't go through diet.deleteFood (there's
    // nothing server-side to target). Mirrors the old single-item delete's
    // three-way branch: drop failed creates locally, defer in-flight creates
    // (runCreate replays the delete once the server id is known), and only
    // hit the API for rows that already have a confirmed id.
    const readyIds = [];
    let deferredCount = 0;
    let droppedCount = 0;

    selected.forEach((id) => {
      const row = rows.find(
        (it) => String(it.deleteId || it.id || it.localId) === String(id)
      );
      if (!row) return;

      const serverId = row.deleteId || row.id;
      const localId = row.localId;
      const status = row._sync?.status;
      const op = row._sync?.op;

      // Instant toolbar/parent calorie update before the network round-trip.
      onPeriodDataChange?.({
        removeFood: {
          periodName,
          optionTab: tab,
          localId,
          rowId: serverId,
          foodId: row.food_id,
          scaled: row.scaled_nutrition,
        },
      });

      if (!serverId && op === "create" && status === "error") {
        // Create never reached the server — nothing to delete remotely.
        pendingOps.current.delete(localId);
        dropItem(tab, localId);
        droppedCount += 1;
        return;
      }

      if (!serverId && op === "create" && status === "syncing") {
        // Create still in flight — defer; runCreate fires the real delete
        // once the server id is known.
        pendingOps.current.set(localId, { type: "delete" });
        patchItem(tab, localId, {
          _sync: { localId, status: "deleting", op: "delete", error: null },
        });
        deferredCount += 1;
        return;
      }

      if (serverId) readyIds.push(serverId);
    });

    setLoading(true);
    try {
      const deletePromises = readyIds.map((id) =>
        diet.deleteFood({ selectedOptionTab: tab, deleteId: id, silent: true })
      );
      const results = await Promise.all(deletePromises);
      setSelectedFoodIds(new Set());
      dirty.current = true;
      await afterRecipeMutation([tab]);

      const apiSuccessCount = results.filter(Boolean).length;
      const successCount = apiSuccessCount + deferredCount + droppedCount;
      const failCount = results.length - apiSuccessCount;
      if (failCount === 0) {
        setAlertMessage?.(
          successCount === 1
            ? "Recipe deleted successfully."
            : `${successCount} recipes deleted successfully.`
        );
        setAlertType?.("success");
      } else if (successCount > 0) {
        setAlertMessage?.(
          `Deleted ${successCount}, failed to delete ${failCount}. Please try again.`
        );
        setAlertType?.("warning");
      } else {
        setAlertMessage?.("Failed to delete recipe(s). Please try again.");
        setAlertType?.("error");
      }
    } finally {
      // Exit select mode only once the whole action (including the reconciling
      // refetch) has settled — flipping it earlier left a window where the
      // toolbar showed the plain trigger buttons while `loading` was still
      // stale-true from this cycle, making a freshly-started mode look like
      // it was already mid-delete.
      setSelectMode(null);
      setLoading(false);
      setDeleteConfirm({ open: false, message: "" });
    }
  };

  const handleDuplicateFood = async (food) => {
    const localId =
      (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
      `tmp-${Date.now()}-${Math.round(Math.random() * 1e6)}`;
    const tab = selectedOptionTab;
    const quantity = food.quantity || 1;
    const unitName = food.quantity_type || null;
    const detail = getRecipeDetails(food);
    const optimistic = {
      ...food,
      deleteId: null,
      id: null,
      localId,
      food_id: getMealFoodId(food),
      quantity,
      quantity_type: unitName,
      quantity_type_id: food.quantity_type_id,
      intake_time: food.intake_time || food.time || "",
      food_details: food.food_details,
      recipe_details: detail || food.recipe_details,
      unitToIdMap: food.unitToIdMap,
      scaled_nutrition: resolveScaledNutrition(
        food,
        detail,
        quantity,
        unitName
      ),
      _sync: { localId, status: "syncing", op: "create", error: null },
      _updated: Date.now(),
    };
    const snapshot = {
      ...food,
      food_id: getMealFoodId(food),
      quantity,
      quantity_type: unitName,
      quantity_type_id: food.quantity_type_id,
      intake_time: optimistic.intake_time,
      food_details: food.food_details,
      unitToIdMap: food.unitToIdMap,
    };

    diet.setSavedFoodItems((prev) => ({
      ...prev,
      [tab]: [...(prev[tab] || []), optimistic],
    }));
    onPeriodDataChange?.({
      patchFood: {
        periodName,
        optionTab: tab,
        food: optimistic,
      },
    });
    setIsOpen(true);
    setAlertMessage?.("Recipe duplicated");
    setAlertType?.("success");
    dirty.current = true;
    enqueue(
      localId,
      withAutoRetry(localId, () => runCreate(localId, tab, optimistic.food_id, snapshot))
    );
  };

  const handleOpenBulkCopy = () => {
    if (selectedFoodIds.size === 0) return;
    const tab = selectedOptionTab;
    const rows = diet.savedFoodItems[tab] || [];
    const selected = Array.from(selectedFoodIds);
    const foods = selected
      .map((id) =>
        rows.find((it) => String(it.deleteId || it.id || it.localId) === String(id))
      )
      .filter(Boolean);
    if (!foods.length) return;
    setCopyAcrossMealTypesModal({ open: true, foods });
  };

  const handleConfirmCopyAcrossMealTypes = async (targets) => {
    const foods = copyAcrossMealTypesModal.foods;
    if (!foods?.length || !targets?.length || !dataByPeriod) return;

    try {
      const results = await Promise.all(
        foods.map((food) =>
          copyFoodToMealTargets({
            eventTypeId: eventTypeIdProp,
            context,
            food,
            sourcePeriodName: periodName,
            targets,
            dataByPeriod,
          })
        )
      );

      dirty.current = true;
      await onPeriodDataChange?.();

      const appliedCount = results.reduce((n, r) => n + r.applied.length, 0);
      const skippedDuplicateCount = results.reduce((n, r) => n + r.skippedDuplicate.length, 0);
      const skippedMissingCount = results.reduce((n, r) => n + r.skippedMissing.length, 0);
      const recipeLabel = `${foods.length} recipe${foods.length === 1 ? "" : "s"}`;

      if (appliedCount > 0 && skippedDuplicateCount === 0 && skippedMissingCount === 0) {
        setAlertMessage?.(
          `Copied ${recipeLabel} to ${targets.length} slot${targets.length === 1 ? "" : "s"}.`
        );
        setAlertType?.("success");
      } else if (appliedCount > 0) {
        const skippedParts = [];
        if (skippedDuplicateCount > 0) skippedParts.push(`${skippedDuplicateCount} already had the recipe`);
        if (skippedMissingCount > 0) skippedParts.push(`${skippedMissingCount} not found`);
        setAlertMessage?.(
          `Copied ${recipeLabel}. Skipped ${skippedParts.join(", ")}.`
        );
        setAlertType?.("warning");
      } else {
        setAlertMessage?.(
          skippedDuplicateCount > 0
            ? "Already has these recipes in every selected slot — nothing copied."
            : "Could not copy recipes to the selected slots."
        );
        setAlertType?.("error");
      }
    } catch (e) {
      console.error("Bulk copy failed:", e);
      setAlertMessage?.("Something went wrong while copying. Please check and try again.");
      setAlertType?.("error");
    } finally {
      setSelectedFoodIds(new Set());
      setSelectMode(null);
    }
  };

  const handleUpdateRecipeItem = (updatedFood) => {
    const tab = selectedOptionTab;
    const localId = updatedFood.localId || String(updatedFood.deleteId || updatedFood.id);
    const quantity = Number(updatedFood.quantity);
    // A row whose create hasn't confirmed yet must KEEP its create marker —
    // overwriting it with an update marker used to wedge the row (the create's
    // retry/error state was destroyed). The edit itself is deferred below.
    // A 'synced' create is done (id in hand) and takes a normal update marker.
    const midCreate =
      updatedFood._sync?.op === "create" &&
      (updatedFood._sync?.status === "syncing" || updatedFood._sync?.status === "error");
    // Apply edited values instantly + mark syncing. Macros are derived from
    // quantity, so rescale them here too — the card reads calories/protein/etc.
    // only from scaled_nutrition, and this optimistic row shadows the server row
    // for the whole sync window. quantity_type_id is included so the row keeps
    // the exact shape a fetched row would have.
    let patchedForParent = null;
    diet.setSavedFoodItems((prev) => ({
      ...prev,
      [tab]: (prev[tab] || []).map((it) => {
        if (it.localId !== localId) return it;
        const detail = getRecipeDetails(it) || getRecipeDetails(updatedFood);
        const unitName = updatedFood.quantity_type || it.quantity_type;
        const scaled = scaleFoodForQuantityEdit(it, detail, quantity, unitName);
        const next = {
          ...it,
          quantity,
          quantity_type: updatedFood.quantity_type,
          quantity_type_id: updatedFood.quantity_type_id || it.quantity_type_id,
          intake_time: updatedFood.intake_time,
          scaled_nutrition: scaled,
          _sync: midCreate
            ? it._sync
            : { localId, status: "syncing", op: "update", error: null },
          _updated: Date.now(),
        };
        patchedForParent = next;
        return next;
      }),
    }));
    if (patchedForParent) {
      onPeriodDataChange?.({
        patchFood: {
          periodName,
          optionTab: tab,
          food: patchedForParent,
        },
      });
    }
    // Latest edit wins: overwrite (never stack) the pending payload.
    latestFoodUpdates.current.set(localId, updatedFood);
    if (!updatedFood.deleteId) {
      // create not confirmed yet — defer; runCreate fires this once the id is known
      pendingOps.current.set(localId, { type: "update", payload: updatedFood });
      return;
    }
    dirty.current = true;
    resetRetries(localId);
    enqueue(localId, withAutoRetry(localId, () => runUpdate(localId, tab)));
  };

  const calculatedValues = useMemo(
    () => diet.calculatedValues,
    [diet.calculatedValues]
  );

  // Hide items whose delete is in flight (kept in state so they still count as
  // pending, but not shown on the card list).
  const savedItems = useMemo(
    () =>
      (diet.savedFoodItems[selectedOptionTab] || []).filter(
        (it) => it._sync?.status !== "deleting"
      ),
    [diet.savedFoodItems, selectedOptionTab]
  );

  // These close over live state, so their identity changes every render, which
  // alone is enough to defeat React.memo on every card. Route the cards through
  // wrappers that never change identity and dispatch to the latest version.
  const handlersRef = useRef(null);
  handlersRef.current = {
    handleUpdateRecipeItem,
    retryFoodSync,
    handleDuplicateFood,
    toggleFoodSelection,
    performBulkDelete,
  };
  const cardHandlers = useMemo(
    () => ({
      updateFoodItem: (...a) => handlersRef.current.handleUpdateRecipeItem(...a),
      retrySync: (...a) => handlersRef.current.retryFoodSync(...a),
      onDuplicate: (...a) => handlersRef.current.handleDuplicateFood(...a),
      toggleSelection: (id) => handlersRef.current.toggleFoodSelection(id),
    }),
    []
  );

  // The Toaster confirm popup re-displays itself whenever onConfirm/onCancel
  // change identity (its effect depends on them) — route it through the same
  // stable-wrapper trick as cardHandlers, or it re-pops on every re-render
  // that happens WHILE the delete is in flight (each onPeriodDataChange call,
  // each setLoading/setSelectedFoodIds).
  const stableConfirmDelete = useMemo(
    () => () => handlersRef.current.performBulkDelete(),
    []
  );
  const stableCancelDelete = useMemo(
    () => () => setDeleteConfirm({ open: false, message: "" }),
    []
  );

  // One card view-model per row object, cached by that object's identity.
  // mergeOptimisticRows hands back the SAME row object when nothing the card
  // renders has changed, so an untouched card keeps the exact same `food` and
  // `onSelect` props and React.memo skips it — only the edited card re-renders.
  const cardModels = useRef(new WeakMap());
  const cards = useMemo(
    () =>
      savedItems.map((item) => {
        const cached = cardModels.current.get(item);
        if (cached && cached.context === context) return cached;
        // Falls back to localId so rows still mid-create (no server id yet)
        // each keep a distinct selectable identity instead of collapsing to
        // the same `undefined` key.
        const rowId = item.deleteId || item.id || item.localId;
        const model = {
          context,
          key: item.localId || String(rowId),
          rowId,
          food: { ...item, deleteId: rowId, context },
          onSelect: () => cardHandlers.toggleSelection(rowId),
        };
        cardModels.current.set(item, model);
        return model;
      }),
    [savedItems, context, cardHandlers]
  );

  const body = (alwaysExpanded || isOpen) && (
          <div className="mb-4" key={`opt-${selectedOptionTab}`}>
            {alwaysExpanded && (
              <div className="flex flex-wrap items-center justify-end gap-2 mb-2">
                {context === "plan" && !selectMode && (
                  <button
                    type="button"
                    onClick={() => setCopyToUserModalOpen(true)}
                    className="text-sm font-medium px-3 py-1.5 rounded-lg border border-orange-primary text-orange-primary bg-white hover:bg-orange-50"
                  >
                    Copy option to another user&apos;s plan
                  </button>
                )}
                {selectMode === "delete" ? (
                  <>
                    <Button
                      onClick={requestBulkDeleteConfirm}
                      text={loading ? "Deleting…" : `Delete (${selectedFoodIds.size})`}
                      icon={<Trash2 className="w-4 h-4" />}
                      className="bg-red-500 text-white text-sm px-3 py-2 hover:bg-red-600"
                      disabled={loading || selectedFoodIds.size === 0 || deleteConfirm.open}
                    />
                    <Button
                      onClick={cancelSelectMode}
                      text="Cancel"
                      className="bg-gray-300 text-black text-sm px-3 py-2 hover:bg-gray-400"
                      disabled={loading || deleteConfirm.open}
                    />
                  </>
                ) : selectMode === "copy" ? (
                  <>
                    <Button
                      onClick={handleOpenBulkCopy}
                      text={`Copy (${selectedFoodIds.size})`}
                      icon={<Copy className="w-4 h-4" />}
                      className="bg-orange-primary text-white text-sm px-3 py-2 hover:bg-orange-600"
                      disabled={selectedFoodIds.size === 0}
                    />
                    <Button
                      onClick={cancelSelectMode}
                      text="Cancel"
                      className="bg-gray-300 text-black text-sm px-3 py-2 hover:bg-gray-400"
                    />
                  </>
                ) : (
                  <>
                    <Button
                      onClick={() => startSelectMode("delete")}
                      text="Delete"
                      icon={<Trash2 className="w-4 h-4" />}
                      iconColor="text-red-600"
                      className="bg-white border border-red-300 text-red-600 text-sm px-3 py-2 hover:bg-red-50"
                      disabled={loading}
                    />
                    {dataByPeriod && (
                      <Button
                        onClick={() => startSelectMode("copy")}
                        text="Copy"
                        icon={<Copy className="w-4 h-4" />}
                        iconColor="text-orange-primary"
                        className="bg-white border border-orange-primary text-orange-primary text-sm px-3 py-2 hover:bg-orange-50"
                        disabled={loading}
                      />
                    )}
                  </>
                )}
              </div>
            )}
            <div className="food-cards-scroll w-full overflow-x-auto">
              <div className="flex gap-4 pb-2 min-w-min">
                {cards.length > 0 ? (
                  cards.map((card) => (
                    <div key={card.key} className="food-card-wrapper flex-shrink-0">
                      <FoodItemCard
                        food={card.food}
                        updateFoodItem={cardHandlers.updateFoodItem}
                        retrySync={cardHandlers.retrySync}
                        setAlertMessage={setAlertMessage}
                        setAlertType={setAlertType}
                        onDuplicate={cardHandlers.onDuplicate}
                        isSelected={selectedFoodIds.has(card.rowId)}
                        onSelect={card.onSelect}
                        showCheckbox={Boolean(selectMode)}
                        intakeTimeReadOnly={slotTimeMode}
                      />
                    </div>
                  ))
                ) : (
                  <div className="text-gray-400 italic p-4 w-full">
                    No recipe items in {selectedOptionTab}
                  </div>
                )}
                {!selectMode && (
                  <div className="flex-shrink-0 self-center">
                    <button
                      onClick={openAddRecipeModal}
                      className="flex items-center justify-center w-32 h-40 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-primary hover:bg-orange-50 transition-colors cursor-pointer"
                      title={`Add recipe to ${periodName}`}
                    >
                      <Plus className="w-6 h-6 text-gray-400 hover:text-orange-primary" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

  return (
    <>
      {summary ? (
        <MealSlotEditorRow
          summaryCard={
            <MealSlotSummaryCard
              {...summary}
              intakeWindow={intakeWindow || { start: "", end: "" }}
              onClick={onSaveIntakeWindow ? () => setSlotTimeModalOpen(true) : undefined}
              disabled={loading}
            />
          }
        >
          {body}
        </MealSlotEditorRow>
      ) : (
        body
      )}

      {slotTimeModalOpen && summary && onSaveIntakeWindow && (
        <MealSlotIntakeTimeModal
          isOpen={slotTimeModalOpen}
          onClose={() => setSlotTimeModalOpen(false)}
          mealTypeName={periodName}
          intakeWindow={intakeWindow}
          onSave={onSaveIntakeWindow}
        />
      )}

      {deleteConfirm.open && (
        <Toaster
          message={deleteConfirm.message}
          onConfirm={stableConfirmDelete}
          onCancel={stableCancelDelete}
          confirmLoading={loading}
        />
      )}

      {copyToUserModalOpen && context === "plan" && (
        <CopyMealOptionToClientPlanModal
          isOpen={copyToUserModalOpen}
          onClose={() => setCopyToUserModalOpen(false)}
          eventTypeId={eventTypeIdProp}
          periodName={periodName}
          optionTitle={selectedOptionTab}
          sourceFoods={savedItems}
          dietCommonUtils={dietCommonUtils}
          onSuccess={(msg) => {
            setAlertMessage?.(msg);
            setAlertType?.("success");
          }}
        />
      )}

      {copyAcrossMealTypesModal.open && dataByPeriod && (
        <CopyFoodAcrossMealTypesModal
          isOpen={copyAcrossMealTypesModal.open}
          onClose={() => setCopyAcrossMealTypesModal({ open: false, foods: [] })}
          foods={copyAcrossMealTypesModal.foods}
          sourcePeriodName={periodName}
          sourceOptionTab={selectedOptionTab}
          dataByPeriod={dataByPeriod}
          onConfirm={handleConfirmCopyAcrossMealTypes}
        />
      )}

      {isStreamlinedModalOpen && (
        <StreamlinedFoodAddModal
          isOpen={isStreamlinedModalOpen}
          onClose={closeStreamlinedModal}
          onAddFood={handleStreamlinedAdd}
          searchInputValue={searchInputValue}
          setSearchInputValue={setSearchInputValue}
          searchResults={searchResults}
          searchLoading={searchLoading}
          searchTotalCount={searchTotalCount}
          foodState={diet.foodState}
          setFoodState={diet.setFoodState}
          onFoodSelect={handleStreamlinedFoodSelect}
          calculatedValues={calculatedValues}
          availableQuantityTypes={diet.foodState.availableQuantityTypes || []}
          hideIntakeTime={slotTimeMode}
        />
      )}
    </>
  );
}

export default DietMealSlotEditor;
